import type { MinimapSpot } from "odyssey-shared";
import type { PartyPositionsBook, PartyOnMap } from "./positions.ts";
import { NO_PARTY, PartyOnMapMemo } from "./positions.ts";
import type { PartySeat } from "./live-spots.ts";
import type { PartyMember } from "./members.ts";
import type { PartySource } from "./store.ts";

/**
 * Where the party's positions go (ADR-100 decisions 10 to 14): the minimap, which draws a member within vision and
 * points the compass at the rest, and the Map, which marks the members on its map and lists the ones elsewhere.
 */
export interface PartyMapSinks {
  readonly minimap: { readonly setParty: (spots: readonly MinimapSpot[], seats?: readonly PartySeat[]) => void };
  readonly worldMap: { readonly setParty: (party: PartyOnMap) => void };
}

/**
 * The minimap's spots for the members on the map. A member's handle is its roster place: party marks are their own kind,
 * so no other handle can meet it.
 *
 * @param party The party as joined to the roster.
 * @returns The spots, in the party's order.
 */
export function spotsOf(party: PartyOnMap): readonly MinimapSpot[] {
  return party.here.map((member) => ({ handle: member.place, x: member.x, y: member.y, name: member.name, place: member.place }));
}

/**
 * The names of the members on the map, which the scene matches the characters it draws against so that a member it
 * draws is marked where it is drawn (P2.12-T35).
 *
 * @param party The party as joined to the roster.
 * @param roster The party's roster, whole.
 * @returns A seat for each member on the map, in the party's order; a member the roster has no row for has none, so no
 *   drawn character is matched to them and they keep the feed's position.
 */
export function seatsOf(party: PartyOnMap, roster: readonly PartyMember[]): readonly PartySeat[] {
  const seats: PartySeat[] = [];
  for (const member of party.here) {
    const row = roster.find((seat) => seat.place === member.place);
    if (row !== undefined) {
      seats.push({ place: member.place, first: member.name, family: row.familyName });
    }
  }
  return seats;
}

/**
 * Joins the positions the page holds to its roster whenever either changes and hands the result to the minimap and the
 * Map, each only when it differs from what they were last given, so a frame that repeats the last, or a roster change
 * that moves no mark, draws nothing. The sinks are told once at the start.
 *
 * @param book The positions the last `partyPositions` frame named.
 * @param party The page's party store.
 * @param sinks The minimap and the Map.
 * @returns A function that stops listening.
 */
export function bindPartyOnMap(book: PartyPositionsBook, party: Pick<PartySource, "subscribe" | "getSnapshot">, sinks: PartyMapSinks): () => void {
  let given: PartyOnMap | null = null;
  const memo = new PartyOnMapMemo();
  const apply = (): void => {
    const snapshot = party.getSnapshot();
    const view = memo.join(book.held(), snapshot.members, snapshot.self);
    if (view === given) {
      return;
    }
    given = view;
    sinks.minimap.setParty(view === NO_PARTY ? [] : spotsOf(view), view === NO_PARTY ? [] : seatsOf(view, snapshot.members));
    sinks.worldMap.setParty(view);
  };
  const stops = [book.subscribe(apply), party.subscribe(apply)];
  apply();
  return () => {
    for (const stop of stops) {
      stop();
    }
  };
}
